/**
 * Locale-aware formatting utilities for dates, times, and numbers.
 *
 * These utilities use the Intl API for locale-sensitive formatting.
 * The locale parameter defaults to 'nl-NL' but can be overridden
 * for internationalization support.
 */

export type SupportedLocale = 'nl-NL' | 'en-US'

const DEFAULT_LOCALE: SupportedLocale = 'nl-NL'

/**
 * Convert a vue-i18n locale code to an Intl locale code.
 */
export function toIntlLocale(locale: string): SupportedLocale {
  const mapping: Record<string, SupportedLocale> = {
    nl: 'nl-NL',
    en: 'en-US',
  }
  return mapping[locale] ?? DEFAULT_LOCALE
}

// -----------------------------------------------------------------------------
// Date Formatting
// -----------------------------------------------------------------------------

/**
 * What every date formatter answers for a date that is absent or unparseable, so a missing
 * date reads the same on every screen.
 */
export const EMPTY_DATE = '—'

export type DateInput = Date | number | string | null | undefined

function formatDateParts(
  date: DateInput,
  options: Intl.DateTimeFormatOptions,
  locale: SupportedLocale
): string {
  const d = toDate(date)
  return d ? new Intl.DateTimeFormat(locale, options).format(d) : EMPTY_DATE
}

/** Format a date as a short date string: "28 feb. 2026" (nl) / "Feb 28, 2026" (en) */
export function formatDateShort(date: DateInput, locale: SupportedLocale = DEFAULT_LOCALE): string {
  return formatDateParts(date, { day: 'numeric', month: 'short', year: 'numeric' }, locale)
}

/** Format a date as a long date string: "28 februari 2026" (nl) / "February 28, 2026" (en) */
export function formatDateLong(date: DateInput, locale: SupportedLocale = DEFAULT_LOCALE): string {
  return formatDateParts(date, { day: 'numeric', month: 'long', year: 'numeric' }, locale)
}

/** Format a time string: "14:30" (nl) / "2:30 PM" (en) */
export function formatTime(date: DateInput, locale: SupportedLocale = DEFAULT_LOCALE): string {
  return formatDateParts(date, { hour: '2-digit', minute: '2-digit' }, locale)
}

/** Format a date with time: "28 februari 2026 om 14:30" (nl) / "February 28, 2026 at 2:30 PM" (en) */
export function formatDateTime(date: DateInput, locale: SupportedLocale = DEFAULT_LOCALE): string {
  return formatDateParts(
    date,
    { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' },
    locale
  )
}

/** Format a date as a relative time string: "2 uur geleden" (nl) / "2 hours ago" (en) */
export function formatRelativeTime(
  date: DateInput,
  locale: SupportedLocale = DEFAULT_LOCALE
): string {
  const d = toDate(date)
  if (!d) return EMPTY_DATE
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)
  const diffYears = Math.floor(diffDays / 365)

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })

  if (diffSeconds < 60) {
    return rtf.format(-diffSeconds, 'second')
  }
  if (diffMinutes < 60) {
    return rtf.format(-diffMinutes, 'minute')
  }
  if (diffHours < 24) {
    return rtf.format(-diffHours, 'hour')
  }
  if (diffDays < 7) {
    return rtf.format(-diffDays, 'day')
  }
  if (diffWeeks < 4) {
    return rtf.format(-diffWeeks, 'week')
  }
  if (diffMonths < 12) {
    return rtf.format(-diffMonths, 'month')
  }
  return rtf.format(-diffYears, 'year')
}

/** Format an appointment's weekday, date and time: "ma 28 feb. 14:30" (nl) / "Mon Feb 28, 2:30 PM" (en) */
export function formatAppointmentDateTime(
  date: DateInput,
  locale: SupportedLocale = DEFAULT_LOCALE
): string {
  return formatDateParts(
    date,
    { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' },
    locale
  )
}

/**
 * Format a date with custom Intl.DateTimeFormat options.
 * Allows full flexibility while maintaining locale awareness.
 */
export function formatDateCustom(
  date: DateInput,
  options: Intl.DateTimeFormatOptions,
  locale: SupportedLocale = DEFAULT_LOCALE
): string {
  return formatDateParts(date, options, locale)
}

/** Format a time range between two dates: "14:30 - 15:30" (nl) / "2:30 PM - 3:30 PM" (en) */
export function formatTimeRange(
  startDate: DateInput,
  endDate: DateInput,
  locale: SupportedLocale = DEFAULT_LOCALE
): string {
  if (!toDate(startDate) || !toDate(endDate)) return EMPTY_DATE
  return `${formatTime(startDate, locale)} - ${formatTime(endDate, locale)}`
}

/** Format a date with weekday: "maandag 28 februari" (nl) / "Monday, February 28" (en) */
export function formatDateWithWeekday(
  date: DateInput,
  locale: SupportedLocale = DEFAULT_LOCALE
): string {
  return formatDateParts(date, { weekday: 'long', day: 'numeric', month: 'long' }, locale)
}

/** Format a date with weekday and year: "maandag 28 februari 2026" (nl) / "Monday, February 28, 2026" (en) */
export function formatDateWithWeekdayAndYear(
  date: DateInput,
  locale: SupportedLocale = DEFAULT_LOCALE
): string {
  return formatDateParts(
    date,
    { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
    locale
  )
}

/** Format a date as two-digit day and month in the locale's order: "28-02-2026" (nl) / "02/28/2026" (en) */
export function formatDateDigits(date: DateInput, locale: SupportedLocale = DEFAULT_LOCALE): string {
  return formatDateParts(date, { day: '2-digit', month: '2-digit', year: 'numeric' }, locale)
}

/**
 * Format a date in DD-MM-YYYY format.
 * Always returns format like "28-02-2026" regardless of locale.
 */
export function formatDateNumeric(date: DateInput): string {
  const d = toDate(date)
  if (!d) return EMPTY_DATE
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  return `${day}-${month}-${d.getFullYear()}`
}

/** Format a date in short weekday format: "ma 28 feb." (nl) / "Mon Feb 28" (en) */
export function formatDateShortWithWeekday(
  date: DateInput,
  locale: SupportedLocale = DEFAULT_LOCALE
): string {
  return formatDateParts(date, { weekday: 'short', day: 'numeric', month: 'short' }, locale)
}

// -----------------------------------------------------------------------------
// Number Formatting
// -----------------------------------------------------------------------------

/** Format a number with locale-appropriate separators: "1.234,56" (nl) / "1,234.56" (en) */
export function formatNumber(
  value: number | undefined | null,
  locale: SupportedLocale = DEFAULT_LOCALE,
  options: Intl.NumberFormatOptions = {}
): string {
  if (value === undefined || value === null) return '-'
  try {
    return new Intl.NumberFormat(locale, options).format(value)
  } catch {
    return '-'
  }
}

/** Format a number as currency: "€ 1.234,56" (nl) / "€1,234.56" (en) */
export function formatCurrency(
  amount: number | undefined | null,
  locale: SupportedLocale = DEFAULT_LOCALE,
  currency = 'EUR'
): string {
  if (amount === undefined || amount === null) return '-'
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(amount)
  } catch {
    return '-'
  }
}

/** Format a number as a percentage: "12,5%" (nl) / "12.5%" (en) */
export function formatPercent(
  value: number | undefined | null,
  locale: SupportedLocale = DEFAULT_LOCALE,
  minimumFractionDigits = 0,
  maximumFractionDigits = 1
): string {
  if (value === undefined || value === null) return '-'
  try {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(value)
  } catch {
    return '-'
  }
}

const BYTES_PER_KB = 1024
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB
const BYTES_PER_GB = BYTES_PER_MB * BYTES_PER_KB

/**
 * Format a byte count as a human-readable file size.
 *
 * Uses binary (1024-based) steps, and one decimal place from KB upwards so the
 * same file renders as the same number on every surface that shows it.
 *
 * The GB step exists for one caller: an EPD dump is a single archive of up to
 * ~2.5 GB (#3771), and "2560,0 MB" on the bar counting it up is a number nobody
 * reads as two and a half gigabytes.
 *
 * nl-NL: "332,5 KB"
 * en-US: "332.5 KB"
 */
export function formatFileSize(
  bytes: number | undefined | null,
  locale: SupportedLocale = DEFAULT_LOCALE
): string {
  if (bytes === undefined || bytes === null || !Number.isFinite(bytes) || bytes < 0) return '-'

  if (bytes < BYTES_PER_KB) {
    return `${formatNumber(bytes, locale, { maximumFractionDigits: 0 })} B`
  }

  const oneDecimal: Intl.NumberFormatOptions = {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }
  if (bytes < BYTES_PER_MB) {
    return `${formatNumber(bytes / BYTES_PER_KB, locale, oneDecimal)} KB`
  }
  if (bytes < BYTES_PER_GB) {
    return `${formatNumber(bytes / BYTES_PER_MB, locale, oneDecimal)} MB`
  }
  return `${formatNumber(bytes / BYTES_PER_GB, locale, oneDecimal)} GB`
}

// -----------------------------------------------------------------------------
// Text Formatting
// -----------------------------------------------------------------------------

/**
 * Truncate text to a maximum length, adding ellipsis if truncated.
 *
 * @param text - The text to truncate
 * @param maxLength - Maximum number of characters before truncation
 * @returns The original text if shorter than maxLength, otherwise truncated with "..."
 *
 * @example
 * truncateText("Hello World", 5) // "Hello..."
 * truncateText("Hi", 5) // "Hi"
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

// -----------------------------------------------------------------------------
// Helper functions
// -----------------------------------------------------------------------------

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Convert a date input to a Date, or null when it is absent or unparseable.
 *
 * A date-only string ("2026-02-28") is a calendar date, so it is read in local time rather than
 * as UTC midnight, which is the previous day anywhere west of Greenwich.
 */
function toDate(input: DateInput): Date | null {
  if (input === null || input === undefined || input === '') return null
  const dateOnly = typeof input === 'string' ? DATE_ONLY.exec(input) : null
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(input)
  return Number.isNaN(d.getTime()) ? null : d
}
